import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, Layers, X, Check, Cpu } from "lucide-react";
import { GithubIcon } from "../common/icons";
import "./styles/projectCard.css";

const ProjectCard = ({ project, index }) => {
	const [showArchModal, setShowArchModal] = useState(false);

	return (
		<>
			<motion.div
				className={`project-card ${project.featured ? "featured" : ""}`}
				initial={{ opacity: 0, y: 24 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.4, delay: index * 0.1 }}
			>
				<div>
					<div className="project-card-header">
						<div>
							<h3 className="project-title">{project.title}</h3>
							<div className="project-subtitle">{project.subtitle}</div>
						</div>
						{project.featured && <span className="project-badge">FEATURED PRODUCT</span>}
					</div>

					<div style={{ marginTop: "16px", marginBottom: "16px" }}>
						<div className="project-problem">
							<strong>Problem:</strong> {project.problem}
						</div>
					</div>

					<p className="project-description">{project.description}</p>

					<div className="project-highlights" style={{ marginTop: "14px" }}>
						{project.highlights.map((item, idx) => (
							<div key={idx} className="project-highlight-item">
								<Check size={14} color="#10b981" style={{ flexShrink: 0, marginTop: "2px" }} />
								<span>{item}</span>
							</div>
						))}
					</div>
				</div>

				<div>
					<div className="project-tech">
						{project.tech.map((t, idx) => (
							<span key={idx} className="tech-pill">
								{t}
							</span>
						))}
					</div>

					<div className="project-footer">
						<div className="project-links">
							{project.githubLink && (
								<a
									href={project.githubLink}
									target="_blank"
									rel="noreferrer"
									className="project-link-btn"
								>
									<GithubIcon size={16} />
									<span>Source Code</span>
								</a>
							)}
							{project.deployedLink && project.deployedLink.startsWith("http") && (
								<a
									href={project.deployedLink}
									target="_blank"
									rel="noreferrer"
									className="project-link-btn"
									style={{ color: "var(--accent-color)" }}
								>
									<ExternalLink size={16} />
									<span>Live Demo</span>
								</a>
							)}
						</div>

						{project.architecture && (
							<button className="arch-btn" onClick={() => setShowArchModal(true)}>
								<Layers size={14} />
								<span>Architecture Flow</span>
							</button>
						)}
					</div>
				</div>
			</motion.div>

			{/* Architecture Flow Modal */}
			<AnimatePresence>
				{showArchModal && project.architecture && (
					<div className="arch-modal-backdrop" onClick={() => setShowArchModal(false)}>
						<motion.div
							className="arch-modal-content"
							initial={{ opacity: 0, scale: 0.9 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.9 }}
							onClick={(e) => e.stopPropagation()}
						>
							<button className="arch-modal-close" onClick={() => setShowArchModal(false)}>
								<X size={20} />
							</button>

							<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
								<Cpu size={18} color="#06b6d4" />
								<h4 style={{ margin: 0, fontSize: "1.25rem", color: "var(--primary-color)" }}>
									{project.title} Architecture
								</h4>
							</div>
							<p style={{ color: "var(--secondary-color)", fontSize: "0.875rem", marginBottom: "24px" }}>
								Component & data flow diagram for backend execution.
							</p>

							<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
								{project.architecture.map((step, idx) => (
									<React.Fragment key={idx}>
										<div
											style={{
												padding: "12px 16px",
												borderRadius: "8px",
												backgroundColor: "var(--bg-card)",
												border: "1px solid var(--border-color)",
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												fontFamily: "var(--code-font)",
												fontSize: "0.84375rem",
											}}
										>
											<span style={{ color: "var(--accent-color)", fontWeight: 600 }}>
												{step.step}
											</span>
											<span style={{ color: "var(--primary-color)" }}>{step.label}</span>
										</div>
										{idx < project.architecture.length - 1 && (
											<div style={{ textAlign: "center", color: "var(--accent-color)", opacity: 0.6 }}>
												↓
											</div>
										)}
									</React.Fragment>
								))}
							</div>
						</motion.div>
					</div>
				)}
			</AnimatePresence>
		</>
	);
};

export default ProjectCard;
